import { Button as ButtonPrimitive } from '@base-ui/react/button';
import React from 'react';

import { controlHeightClasses, controlIconClasses, controlSquareClasses } from '@playbooks/ui/components/control-size';
import type { IconValue } from '@playbooks/ui/components/icon';
import { Icon, resolveIcon } from '@playbooks/ui/components/icon';
import { Spinner } from '@playbooks/ui/components/spinner';
import { cn } from '@playbooks/ui/lib/utils';

import { cva, VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
	'group/button relative inline-flex shrink-0 items-center justify-center rounded-control border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-colors outline-none select-none focus-visible:border-focus focus-visible:ring-3 focus-visible:ring-focus/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-danger aria-invalid:ring-3 aria-invalid:ring-danger/20 dark:aria-invalid:border-danger/50 dark:aria-invalid:ring-danger/40 [&_svg]:pointer-events-none [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				default: 'bg-action text-action-foreground hover:bg-action-hover',
				tab: 'rounded-none! border-x-0 border-t-0 border-b-2! border-b-transparent bg-transparent text-foreground-nav hover:bg-surface-hover hover:text-foreground aria-expanded:bg-surface-hover aria-expanded:text-foreground data-[active=true]:border-b-foreground! data-[active=true]:bg-transparent! data-[active=true]:text-foreground',
				'tab-lifted':
					'rounded-b-none! border-border-strong bg-surface text-foreground-nav hover:bg-surface-hover hover:text-foreground data-[active=true]:border-b-surface-control data-[active=true]:bg-surface-control data-[active=true]:text-foreground',
				'tab-contained':
					'border-transparent bg-transparent text-foreground-nav hover:bg-surface-hover hover:text-foreground aria-expanded:bg-surface-hover aria-expanded:text-foreground data-[active=true]:border-border-strong data-[active=true]:bg-surface-hover data-[active=true]:text-foreground',
				outline:
					'border-border-strong bg-surface-control text-foreground hover:bg-surface-hover aria-expanded:bg-surface-hover aria-expanded:text-foreground data-[active=true]:bg-surface-hover data-[active=true]:text-foreground',
				secondary:
					'bg-transparent text-foreground hover:bg-surface-hover aria-expanded:bg-surface-hover aria-expanded:text-foreground data-[active=true]:bg-surface-hover data-[active=true]:text-foreground',
				accent: 'bg-surface-selected text-foreground hover:bg-surface-hover aria-expanded:bg-surface-selected',
				ghost:
					'text-foreground-nav transition-colors duration-200 ease-out hover:bg-surface-hover hover:text-foreground aria-expanded:bg-surface-hover aria-expanded:text-foreground data-[active=true]:bg-surface-hover data-[active=true]:text-foreground',
				destructive:
					'bg-danger text-foreground-inverse hover:brightness-90 focus-visible:border-danger/40 focus-visible:ring-danger/30 dark:bg-danger dark:text-foreground dark:hover:brightness-90 dark:focus-visible:ring-danger/40',
				link: 'text-foreground underline-offset-4 hover:underline',
			},
			size: {
				default: `${controlHeightClasses.default} ${controlIconClasses.default} gap-2.5 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3`,
				xs: `${controlHeightClasses.xs} ${controlIconClasses.xs} gap-1.5 px-2.5 text-sm has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2`,
				sm: `${controlHeightClasses.sm} ${controlIconClasses.sm} gap-2 px-3 text-sm has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5`,
				lg: `${controlHeightClasses.lg} ${controlIconClasses.lg} gap-2.5 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3`,
				icon: `${controlSquareClasses.default} ${controlIconClasses.default}`,
				'icon-xs': `${controlSquareClasses.xs} ${controlIconClasses.xs}`,
				'icon-sm': `${controlSquareClasses.sm} ${controlIconClasses.sm}`,
				'icon-lg': `${controlSquareClasses.lg} ${controlIconClasses.lg}`,
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);
type ControlIconValue = IconValue;
type ControlImageValue =
	| string
	| {
			src: string;
			alt?: string;
			className?: string;
	  };
type ButtonProps = ButtonPrimitive.Props &
	VariantProps<typeof buttonVariants> & {
		isLoading?: boolean;
		loadingLabel?: string;
		prevIcon?: ControlIconValue | null;
		prevImg?: ControlImageValue | null;
		nextIcon?: ControlIconValue | null;
		'data-active'?: boolean | string;
	};
const ControlIcon = ({ value, position }: { value: ControlIconValue; position: 'inline-start' | 'inline-end' }) => {
	return <Icon data-icon={position} {...resolveIcon(value)} aria-hidden />;
};
const Button = ({
	className,
	variant = 'default',
	size = 'default',
	isLoading = false,
	loadingLabel = 'Loading',
	prevIcon,
	prevImg,
	nextIcon,
	disabled,
	children,
	'aria-label': ariaLabel,
	'data-active': dataActive,
	...props
}: ButtonProps) => {
	const loading = isLoading;
	const image = typeof prevImg === 'string' ? { src: prevImg, alt: '' } : prevImg;

	// Theme
	const classNames = {
		root: cn(buttonVariants({ variant, size }), className),
		image: cn('size-5 shrink-0 rounded-control object-cover', image?.className),
	};

	// Props
	const content = (
		<>
			{image && <img data-icon='inline-start' src={image.src} alt={image.alt || ''} className={classNames.image} />}
			{prevIcon && <ControlIcon value={prevIcon} position='inline-start' />}
			{children}
			{nextIcon && <ControlIcon value={nextIcon} position='inline-end' />}
		</>
	);
	return (
		<ButtonPrimitive
			data-slot='button'
			data-active={dataActive || undefined}
			aria-busy={loading || undefined}
			aria-label={loading ? loadingLabel : ariaLabel}
			disabled={disabled || loading}
			{...props}
			className={classNames.root}>
			{loading ? (
				<>
					<span aria-hidden className='invisible flex items-center gap-[inherit]'>
						{content}
					</span>
					<span aria-hidden className='absolute inset-0 flex items-center justify-center'>
						<Spinner />
					</span>
				</>
			) : (
				content
			)}
		</ButtonPrimitive>
	);
};
export { Button, buttonVariants };
export type { ButtonProps, ControlIconValue, ControlImageValue };
